import type { PDFDocument, PDFPage, Rotation } from 'pdf-lib'
import type { ToolLayoutPreset, ToolPageCountBucket } from '@saas-pages/shared/public/runtime'

export const MAX_FILE_MB = 100
export const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024
export const MAX_SOURCE_PAGES = 400
export const MAX_OUTPUT_SHEETS = 200

export const PAGES_PER_SHEET = [2, 4, 8] as const
export type PagesPerSheet = (typeof PAGES_PER_SHEET)[number]
export const PAPER_SIZES = ['a4', 'a3'] as const
export type PaperSize = (typeof PAPER_SIZES)[number]
export const ORIENTATIONS = ['portrait', 'landscape'] as const
export type Orientation = (typeof ORIENTATIONS)[number]
export const PAGE_ORDERS = ['across', 'down'] as const
export type PageOrder = (typeof PAGE_ORDERS)[number]
export const MARGIN_OPTIONS_MM = [0, 5, 10, 15] as const
export type MarginMm = (typeof MARGIN_OPTIONS_MM)[number]
export const GAP_OPTIONS_MM = [0, 3, 5, 10] as const
export type GapMm = (typeof GAP_OPTIONS_MM)[number]

export interface NUpOptions {
  pagesPerSheet: PagesPerSheet
  paperSize: PaperSize
  orientation: Orientation
  marginMm: MarginMm
  gapMm: GapMm
  pageOrder: PageOrder
}

export interface SheetDimensions {
  widthPt: number
  heightPt: number
  widthMm: number
  heightMm: number
}

export interface PagePlacement {
  sourcePageIndex: number
  sheetIndex: number
  slotIndex: number
  row: number
  column: number
  x: number
  y: number
  width: number
  height: number
}

export interface NUpResult {
  bytes: Uint8Array
  sourcePages: number
  outputSheets: number
  dimensions: SheetDimensions
}

export interface EffectivePageGeometry {
  box: { left: number; bottom: number; right: number; top: number }
  width: number
  height: number
  visualWidth: number
  visualHeight: number
  rotation: 0 | 90 | 180 | 270
}

const MM_TO_PT = 72 / 25.4
const PAPER_DIMENSIONS_MM: Record<PaperSize, readonly [number, number]> = {
  a4: [210, 297],
  a3: [297, 420],
}

export class NUpPdfError extends Error {
  constructor(
    readonly code: 'too_large' | 'too_many_pages' | 'too_many_sheets' | 'invalid_layout' | 'invalid_input' | 'encrypted' | 'parse_error',
  ) {
    super(code)
    this.name = 'NUpPdfError'
  }
}

export function getEffectivePageGeometry(page: PDFPage): EffectivePageGeometry {
  const media = page.getMediaBox()
  const crop = page.getCropBox()
  const left = Math.max(media.x, crop.x)
  const bottom = Math.max(media.y, crop.y)
  const right = Math.min(media.x + media.width, crop.x + crop.width)
  const top = Math.min(media.y + media.height, crop.y + crop.height)
  const width = right - left
  const height = top - bottom
  if (width <= 0 || height <= 0) throw new NUpPdfError('parse_error')

  const rotation = normalizeRotation(page.getRotation())
  return {
    box: { left, bottom, right, top },
    width,
    height,
    visualWidth: rotation === 90 || rotation === 270 ? height : width,
    visualHeight: rotation === 90 || rotation === 270 ? width : height,
    rotation,
  }
}

function normalizeRotation(rotation: Rotation): 0 | 90 | 180 | 270 {
  const normalized = ((rotation.angle % 360) + 360) % 360
  if (normalized === 0 || normalized === 90 || normalized === 180 || normalized === 270) {
    return normalized
  }
  throw new NUpPdfError('invalid_input')
}

export function getSheetDimensions(
  paperSize: PaperSize,
  orientation: Orientation,
): SheetDimensions {
  const [portraitWidth, portraitHeight] = PAPER_DIMENSIONS_MM[paperSize]
  const [widthMm, heightMm] = orientation === 'portrait'
    ? [portraitWidth, portraitHeight]
    : [portraitHeight, portraitWidth]

  return {
    widthPt: widthMm * MM_TO_PT,
    heightPt: heightMm * MM_TO_PT,
    widthMm,
    heightMm,
  }
}

export function getGrid(
  pagesPerSheet: PagesPerSheet,
  orientation: Orientation,
): { columns: number; rows: number } {
  if (pagesPerSheet === 4) return { columns: 2, rows: 2 }
  if (pagesPerSheet === 2) {
    return orientation === 'portrait'
      ? { columns: 1, rows: 2 }
      : { columns: 2, rows: 1 }
  }
  return orientation === 'portrait'
    ? { columns: 2, rows: 4 }
    : { columns: 4, rows: 2 }
}

export function buildLayoutPreset(options: NUpOptions): ToolLayoutPreset {
  return [
    `${options.pagesPerSheet}up`,
    options.paperSize,
    options.orientation,
    `m${options.marginMm}`,
    `g${options.gapMm}`,
    options.pageOrder,
  ].join('_') as ToolLayoutPreset
}

export function pageCountBucket(pageCount: number): ToolPageCountBucket {
  if (pageCount <= 10) return '1-10'
  if (pageCount <= 50) return '11-50'
  if (pageCount <= 100) return '51-100'
  return '101-400'
}

export function nUpFilename(originalName: string): string {
  const base = originalName.toLowerCase().endsWith('.pdf')
    ? originalName.slice(0, -4)
    : originalName
  return `${base}-neskolko-stranits-na-liste.pdf`
}

export function validateInputSize(size: number): void {
  if (size > MAX_FILE_BYTES) throw new NUpPdfError('too_large')
}

export function calculatePlacement(
  sourcePageIndex: number,
  sourceWidth: number,
  sourceHeight: number,
  options: NUpOptions,
): PagePlacement {
  const dimensions = getSheetDimensions(options.paperSize, options.orientation)
  const { columns, rows } = getGrid(options.pagesPerSheet, options.orientation)
  const margin = options.marginMm * MM_TO_PT
  const gap = options.gapMm * MM_TO_PT
  const cellWidth = (dimensions.widthPt - margin * 2 - gap * (columns - 1)) / columns
  const cellHeight = (dimensions.heightPt - margin * 2 - gap * (rows - 1)) / rows

  if (cellWidth <= 0 || cellHeight <= 0 || sourceWidth <= 0 || sourceHeight <= 0) {
    throw new NUpPdfError('invalid_layout')
  }

  const slotIndex = sourcePageIndex % options.pagesPerSheet
  const row = options.pageOrder === 'across'
    ? Math.floor(slotIndex / columns)
    : slotIndex % rows
  const column = options.pageOrder === 'across'
    ? slotIndex % columns
    : Math.floor(slotIndex / rows)
  const scale = Math.min(cellWidth / sourceWidth, cellHeight / sourceHeight)
  const width = sourceWidth * scale
  const height = sourceHeight * scale
  const cellX = margin + column * (cellWidth + gap)
  const cellY = dimensions.heightPt - margin - (row + 1) * cellHeight - row * gap

  return {
    sourcePageIndex,
    sheetIndex: Math.floor(sourcePageIndex / options.pagesPerSheet),
    slotIndex,
    row,
    column,
    x: cellX + (cellWidth - width) / 2,
    y: cellY + (cellHeight - height) / 2,
    width,
    height,
  }
}

export function getRotatedDrawOptions(
  placement: Pick<PagePlacement, 'x' | 'y' | 'width' | 'height'>,
  rotation: 0 | 90 | 180 | 270,
): {
  x: number
  y: number
  width: number
  height: number
  rotation: 0 | 90 | 180 | 270
} {
  if (rotation === 90) {
    return {
      x: placement.x + placement.width,
      y: placement.y,
      width: placement.height,
      height: placement.width,
      rotation,
    }
  }
  if (rotation === 180) {
    return {
      x: placement.x + placement.width,
      y: placement.y + placement.height,
      width: placement.width,
      height: placement.height,
      rotation,
    }
  }
  if (rotation === 270) {
    return {
      x: placement.x,
      y: placement.y + placement.height,
      width: placement.height,
      height: placement.width,
      rotation,
    }
  }
  return { ...placement, rotation }
}

export async function createNUpPdf(
  input: Uint8Array | ArrayBuffer,
  options: NUpOptions,
): Promise<NUpResult> {
  validateInputSize(input.byteLength)
  const { PDFArray, PDFDocument, degrees } = await import('pdf-lib')
  let source: PDFDocument

  try {
    source = await PDFDocument.load(input)
  } catch (error) {
    const message = error instanceof Error ? error.message.toLowerCase() : ''
    if (message.includes('encrypted') || message.includes('password')) {
      throw new NUpPdfError('encrypted')
    }
    throw new NUpPdfError('parse_error')
  }

  const sourcePages = source.getPageCount()
  if (sourcePages <= 0) throw new NUpPdfError('invalid_input')
  if (sourcePages > MAX_SOURCE_PAGES) throw new NUpPdfError('too_many_pages')
  const outputSheets = Math.ceil(sourcePages / options.pagesPerSheet)
  if (outputSheets > MAX_OUTPUT_SHEETS) throw new NUpPdfError('too_many_sheets')

  const output = await PDFDocument.create()
  const dimensions = getSheetDimensions(options.paperSize, options.orientation)
  let currentSheet: PDFPage | null = null

  for (let sourcePageIndex = 0; sourcePageIndex < sourcePages; sourcePageIndex += 1) {
    if (sourcePageIndex % options.pagesPerSheet === 0) {
      currentSheet = output.addPage([dimensions.widthPt, dimensions.heightPt])
    }
    if (!currentSheet) throw new NUpPdfError('parse_error')

    const sourcePage = source.getPage(sourcePageIndex)
    const contents = sourcePage.node.Contents()
    if (!contents || (contents instanceof PDFArray && contents.size() === 0)) continue

    const geometry = getEffectivePageGeometry(sourcePage)
    const embeddedPage = await output.embedPage(sourcePage, geometry.box)
    const placement = calculatePlacement(
      sourcePageIndex,
      geometry.visualWidth,
      geometry.visualHeight,
      options,
    )
    const draw = getRotatedDrawOptions(placement, geometry.rotation)
    currentSheet.drawPage(embeddedPage, {
      x: draw.x,
      y: draw.y,
      width: draw.width,
      height: draw.height,
      rotate: degrees(draw.rotation),
    })
  }

  return {
    bytes: await output.save({ useObjectStreams: true }),
    sourcePages,
    outputSheets,
    dimensions,
  }
}
